import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@nrwl/environment';
import { EMPTY, Observable, of, forkJoin } from 'rxjs';
import { catchError, expand, map, reduce } from 'rxjs/operators';
import { parseLocalDateString } from '../../../helpers/date-utils';

export interface SoapNoteItem {
  id?: number | string;
  date?: string;
  employee?: string;
  priority?: number | string;
  note?: string;
  assigned_attributes?: string[];
  plan_fields?: string[];
  plan_items?: any[];
  plan_items_quantity?: unknown[] | number | string;
}

export interface SoapNotesPayload {
  subjective: SoapNoteItem[];
  objective: SoapNoteItem[];
  assessment: SoapNoteItem[];
  plan: SoapNoteItem[];
}

export interface GetSoapNotesParams {
  readonly customerId: string;
  readonly treatmentId: string;
}

export interface SoapGroup {
  id: string;
  name: string;
}

export type SoapCategory = 'S' | 'O' | 'A' | 'P';

export interface SoapAttribute {
  id: string;
  name: string;
  groupIds?: string[];
  soapCategory?: SoapCategory;
}

export interface SoapAttributeSelection {
  id: string;
  name: string;
}

export interface SoapPlanItem {
  Id: string;
  InvMasterId?: string;
  ApptDetailId?: string;
  TreatmentId?: string;
  TreatmentPlanItemId?: string;
  ItemDescription?: string;
  AddonQty?: number | string | null;
  InventoryShort?: string;
  InvType?: string;
  id: string;
  name: string;
}

export interface TreatmentSoapRecord {
  Id?: string | number;
  CustomerId?: string | number;
  CompanyId?: string | number;
  TreatmentId?: string | number;
  NoteType?: string | number;
  NoteDate?: string | Date;
  Note?: string;
  EmployeeId?: string | number;
  PriorityLevel?: number | string;
  PlanItems?: unknown;
  PlanItemsQuantity?: unknown;
}

export interface TreatmentSoapPlanItemRecord {
  Id?: string | number;
  InvMasterId?: string | number;
  ApptDetailId?: string | number;
  TreatmentId?: string | number;
  ItemDescription?: string;
  InventoryShort?: string;
  AddonQty?: number | string | null;
}

export interface SoapPlanItemCatalogRecord {
  InvMasterId?: string | number;
  InventoryShort?: string;
  ItemDescription?: string;
  InvType?: string;
}

export interface SoapAttributesByCategory {
  subjective: string[];
  objective: string[];
  assessment: string[];
  plan: string[];
}

export interface TreatmentSoapAttributeLinkRecord {
  Id?: string | number;
  TreatmentId?: string | number;
  SoapAttributeId?: string | number;
  SoapAttributeName?: string;
  IsChecked?: boolean;
}

type SoapSection = keyof SoapNotesPayload;

export function normalizePlanItemLabel(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value).trim();
}

/**
 * Plan items are labelled by the PNE "Service Id" (InvMaster.InventoryShort), which is the
 * label the Simple SOAP screen shows. ItemDescription is only a fallback for records that
 * do not carry it.
 */
export function resolvePlanItemName(inventoryShort: unknown, itemDescription: unknown): string {
  return normalizePlanItemLabel(inventoryShort) || normalizePlanItemLabel(itemDescription);
}

@Injectable({ providedIn: 'root' })
export class SoapNotesService {
  private readonly http = inject(HttpClient);

  public getSoapNotes(params: GetSoapNotesParams): Observable<SoapNotesPayload> {
    const queryParams: Record<string, string> = {
      CustomerId: params.customerId,
      TreatmentId: params.treatmentId,
    };

    return this.http
      .get<TreatmentSoapRecord[] | Record<string, unknown>>(
        `${environment.baseAPIUrlMicro}egress-proxy/pne/treatmentsoap`,
        {
          params: queryParams,
        },
      )
      .pipe(map((response) => this.parseSoapNotesResponse(response)));
  }

  public getSoapGroupsCatalog(): Observable<SoapGroup[]> {
    const url = `${environment.baseAPIUrlMicro}egress-proxy/pne/SOAPGroup`;
    return this.fetchAllPages(url, {}, (item) => this.mapSoapGroup(item), 200);
  }

  public getSoapAttributesCatalog(): Observable<SoapAttribute[]> {
    const url = `${environment.baseAPIUrlMicro}egress-proxy/pne/SOAPAttribute`;
    return this.fetchAllPages(url, {}, (item) => this.mapSoapAttribute(item), 200);
  }

  public getPlanItemsCatalog(): Observable<SoapPlanItem[]> {
    const url = `${environment.baseAPIUrlMicro}egress-proxy/pne/TreatmentSOAPPlanItems/GetRetailAndServicesFlaggedAsPlanItems`;
    return this.fetchAllPages(url, {}, (item) => this.mapPlanItemCatalog(item), 200);
  }

  public getPlanItemsForTreatment(treatmentId: string): Observable<SoapPlanItem[]> {
    const url = `${environment.baseAPIUrlMicro}egress-proxy/pne/TreatmentSOAPPlanItems`;
    return this.fetchAllPages(
      url,
      { TreatmentId: treatmentId },
      (item) => this.mapAssignedPlanItem(item),
      200,
    );
  }

  private parseSoapNotesResponse(
    response: TreatmentSoapRecord[] | Record<string, unknown>,
  ): SoapNotesPayload {
    const data =
      (Array.isArray(response) && response) ||
      (response as Record<string, unknown>)?.['Data'] ||
      (response as Record<string, unknown>)?.['data'] ||
      response;

    if (Array.isArray(data)) {
      return this.parseNotesArray(data as TreatmentSoapRecord[]);
    }

    if (data && typeof data === 'object') {
      return this.parseNotesObject(data as Record<string, unknown>);
    }

    return this.emptyPayload();
  }

  private parseNotesArray(
    items: TreatmentSoapRecord[],
    fixedSection?: SoapSection,
  ): SoapNotesPayload {
    const payload = this.emptyPayload();

    items.forEach((item) => {
      const section = fixedSection ?? this.resolveSection(item);

      if (!section) {
        return;
      }

      const note = this.buildNote(item);

      if (note) {
        payload[section].push(note);
      }
    });

    return payload;
  }

  private parseNotesObject(data: Record<string, unknown>): SoapNotesPayload {
    const payload = this.emptyPayload();

    const sectionMap: Array<[SoapSection, unknown]> = [
      ['subjective', data['Subjective']],
      ['objective', data['Objective']],
      ['assessment', data['Assessment']],
      ['plan', data['Plan']],
    ];

    sectionMap.forEach(([section, value]) => {
      if (Array.isArray(value)) {
        const parsed = this.parseNotesArray(value, section);
        payload[section] = parsed[section];
      }
    });

    return payload;
  }

  private resolveSection(item: TreatmentSoapRecord): SoapSection | null {
    const typeValue = item.NoteType;

    if (typeof typeValue === 'number') {
      if (typeValue === 1) return 'subjective';
      if (typeValue === 2) return 'objective';
      if (typeValue === 3) return 'assessment';
      if (typeValue === 4) return 'plan';
    }

    if (typeof typeValue !== 'string') {
      return null;
    }

    const normalized = typeValue.toLowerCase();

    if (normalized.includes('subjective') || normalized === 's' || normalized === 'subj') {
      return 'subjective';
    }
    if (normalized.includes('objective') || normalized === 'o' || normalized === 'obj') {
      return 'objective';
    }
    if (normalized.includes('assessment') || normalized === 'a' || normalized === 'assess') {
      return 'assessment';
    }
    if (normalized.includes('plan') || normalized === 'p') {
      return 'plan';
    }

    return null;
  }

  private buildNote(item: TreatmentSoapRecord): SoapNoteItem | null {
    const idValue = item.Id;
    const id = typeof idValue === 'string' || typeof idValue === 'number' ? idValue : undefined;

    const dateValue = item.NoteDate;
    const date = this.normalizeDate(dateValue);

    const employeeValue = item.EmployeeId;
    const employee = this.normalizeString(employeeValue);

    const priorityValue = item.PriorityLevel;
    const priority = this.normalizePriority(priorityValue);

    const noteValue = item.Note;
    const note = this.normalizeString(noteValue);

    const planItems = this.normalizePlanItems(item.PlanItems);
    const planItemsQuantity = this.normalizePlanItemsQuantity(item.PlanItemsQuantity);

    const notePayload: SoapNoteItem = {
      ...(id !== undefined ? { id } : {}),
      ...(date ? { date } : {}),
      ...(employee ? { employee } : {}),
      ...(priority !== null ? { priority } : {}),
      ...(note ? { note } : {}),
      ...(planItems.length ? { plan_items: planItems } : {}),
      ...(planItemsQuantity !== null ? { plan_items_quantity: planItemsQuantity } : {}),
    };

    return Object.keys(notePayload).length > 0 ? notePayload : null;
  }

  private normalizeDate(value: unknown): string {
    if (!value) {
      return '';
    }

    return parseLocalDateString(value as any);
  }

  private normalizeString(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }

  private normalizePriority(value: unknown): number | string | null {
    if (value === null || value === undefined || value === '') {
      return -1;
    }

    if (typeof value === 'number') {
      if (Number.isNaN(value)) {
        return -1;
      }
      if (value === 0 || value === 1 || value === 2 || value === -1) {
        return value;
      }
      return -1;
    }

    const normalized = String(value).trim();
    if (!normalized) {
      return -1;
    }

    const lowered = normalized.toLowerCase();
    if (lowered === 'none') {
      return -1;
    }
    if (lowered === 'low') {
      return 0;
    }
    if (lowered === 'medium') {
      return 1;
    }
    if (lowered === 'high') {
      return 2;
    }

    const asNumber = Number(normalized);
    if (!Number.isNaN(asNumber)) {
      if (asNumber === 0 || asNumber === 1 || asNumber === 2 || asNumber === -1) {
        return asNumber;
      }
      return -1;
    }

    return -1;
  }

  private normalizePlanItems(value: unknown): any[] {
    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const normalized = value.trim();
      return normalized ? [normalized] : [];
    }

    return [];
  }

  private normalizePlanItemsQuantity(value: unknown): unknown[] | number | string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === 'number') {
      return Number.isNaN(value) ? null : value;
    }

    const normalized = String(value).trim();
    if (!normalized) {
      return null;
    }

    const asNumber = Number(normalized);
    if (!Number.isNaN(asNumber)) {
      return asNumber;
    }

    return normalized;
  }

  private emptyPayload(): SoapNotesPayload {
    return {
      subjective: [],
      objective: [],
      assessment: [],
      plan: [],
    };
  }

  public getSoapAttributes(params: GetSoapNotesParams): Observable<SoapAttributesByCategory> {
    const url = `${environment.baseAPIUrlMicro}egress-proxy/pne/treatmentSOAPattributelink`;
    const base: SoapAttributesByCategory = {
      subjective: [],
      objective: [],
      assessment: [],
      plan: [],
    };
    const links$ = this.fetchAllPages(
      url,
      { TreatmentId: params.treatmentId, Sort: 'Id' },
      (item) => this.mapSoapAttributeLink(item),
      200,
    );
    const catalog$ = this.getSoapAttributesCatalog().pipe(
      catchError(() => of([] as SoapAttribute[])),
    );

    return forkJoin({ links: links$, catalog: catalog$ }).pipe(
      map(({ links, catalog }) => {
        const categoryById = new Map(catalog.map((attr) => [String(attr.id), attr.soapCategory]));
        const acc: SoapAttributesByCategory = {
          subjective: [],
          objective: [],
          assessment: [],
          plan: [],
        };
        links.forEach((item) => {
          if (!item.IsChecked) {
            return;
          }
          const id = item.SoapAttributeId;
          if (!id) {
            return;
          }
          const normalized = String(id);
          const category = categoryById.get(normalized);
          if (category === 'S') {
            acc.subjective.push(normalized);
          } else if (category === 'O') {
            acc.objective.push(normalized);
          } else if (category === 'A') {
            acc.assessment.push(normalized);
          } else if (category === 'P') {
            acc.plan.push(normalized);
          }
        });
        return acc;
      }),
      catchError(() => of(base)),
    );
  }

  private fetchAllPages<T>(
    url: string,
    params: Record<string, string>,
    mapper: (item: unknown) => T | null,
    pageSize = 200,
  ): Observable<T[]> {
    const fetchPage = (page: number) =>
      this.http
        .get<unknown>(url, {
          params: { ...params, Page: String(page), Rows: String(pageSize) },
          observe: 'response',
        })
        .pipe(
          map((response) => {
            const rawItems = this.normalizePagedItems(response.body);
            const items = rawItems.map((item) => mapper(item)).filter(Boolean) as T[];
            const totalPages = this.getTotalPages(response.headers, pageSize, rawItems.length);
            return { items, totalPages, count: rawItems.length, page, error: false };
          }),
          catchError(() => of({ items: [], totalPages: 0, count: 0, page, error: true })),
        );

    return fetchPage(1).pipe(
      expand(({ totalPages, count, page, error }) => {
        if (error) {
          return EMPTY;
        }
        const nextPage = page + 1;
        const hasMore = totalPages ? nextPage <= totalPages : count === pageSize;
        return hasMore ? fetchPage(nextPage) : EMPTY;
      }),
      map((result) => result.items),
      reduce((acc, items) => acc.concat(items), [] as T[]),
    );
  }

  private normalizePagedItems(body: unknown): unknown[] {
    if (Array.isArray(body)) {
      return body;
    }

    if (body && typeof body === 'object') {
      const record = body as Record<string, unknown>;
      const data = record['Data'] ?? record['data'];
      return Array.isArray(data) ? data : [];
    }

    return [];
  }

  private mapSoapAttributeLink(item: unknown): TreatmentSoapAttributeLinkRecord | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const record = item as Record<string, unknown>;
    const attributeId = record['SoapAttributeId'];
    if (!attributeId) {
      return null;
    }

    return {
      Id: record['Id'] !== undefined && record['Id'] !== null ? String(record['Id']) : undefined,
      TreatmentId:
        record['TreatmentId'] !== undefined && record['TreatmentId'] !== null
          ? String(record['TreatmentId'])
          : undefined,
      SoapAttributeId: String(attributeId),
      SoapAttributeName: record['SoapAttributeName']
        ? String(record['SoapAttributeName'])
        : undefined,
      IsChecked: record['IsChecked'] === true,
    };
  }

  public getTotalPages(headers: any, pageSize: number, fallbackCount: number): number | null {
    if (!headers?.get) {
      return fallbackCount === pageSize ? null : 0;
    }

    const direct = this.getHeaderNumber(headers, [
      'X-Total-Pages',
      'X-Total-Page',
      'X-Pages',
      'X-Page-Count',
    ]);
    if (direct) {
      return direct;
    }

    const totalCount = this.getHeaderNumber(headers, ['X-Total-Count', 'Total-Count']);
    if (totalCount) {
      return Math.ceil(totalCount / pageSize);
    }

    const pagination = headers.get('X-Pagination') || headers.get('Pagination');
    if (pagination) {
      try {
        const parsed = JSON.parse(pagination);
        const total = Number(parsed?.totalPages ?? parsed?.TotalPages);
        if (!Number.isNaN(total) && total > 0) {
          return total;
        }
      } catch (error) {
        console.warn('[SOAP Notes] Failed to parse pagination header', pagination, error);
        return fallbackCount === pageSize ? null : 0;
      }
    }

    return fallbackCount === pageSize ? null : 0;
  }

  public getHeaderNumber(headers: any, names: string[]): number | null {
    for (const name of names) {
      const raw = headers.get(name);
      if (!raw) {
        continue;
      }

      const value = Number(raw);
      if (!Number.isNaN(value) && value > 0) {
        return value;
      }
    }

    return null;
  }

  private mapSoapGroup(item: unknown): SoapGroup | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const record = item as Record<string, unknown>;
    const id = record['Id'];
    const name = record['Name'];

    if (!id || !name) {
      return null;
    }

    return { id: String(id), name: String(name) };
  }

  private mapSoapAttribute(item: unknown): SoapAttribute | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const record = item as Record<string, unknown>;
    const id = record['Id'];
    const name = record['Name'];
    const soapCategory = this.normalizeSoapCategory(record['SoapCategory']);
    const groups = Array.isArray(record['Groups']) ? (record['Groups'] as unknown[]) : [];
    const groupIds = groups
      .map((group) => {
        if (!group || typeof group !== 'object') {
          return '';
        }
        const groupRecord = group as Record<string, unknown>;
        const groupValue = groupRecord['Id'];
        return groupValue ? String(groupValue) : '';
      })
      .filter((value) => value);

    if (!id || !name) {
      return null;
    }

    const normalizedGroupIds = groupIds.length ? groupIds : undefined;
    return {
      id: String(id),
      name: String(name),
      groupIds: normalizedGroupIds,
      soapCategory,
    };
  }

  private normalizeSoapCategory(value: unknown): SoapCategory | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }

    const normalized = value.trim().toUpperCase();
    if (normalized === 'S' || normalized === 'O' || normalized === 'A' || normalized === 'P') {
      return normalized;
    }

    return undefined;
  }

  private mapPlanItemCatalog(item: unknown): SoapPlanItem | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const record = item as SoapPlanItemCatalogRecord;
    const idValue = record.InvMasterId;
    const inventoryShort = normalizePlanItemLabel(record.InventoryShort);
    const itemDescription = normalizePlanItemLabel(record.ItemDescription);
    const name = resolvePlanItemName(inventoryShort, itemDescription);
    if (!idValue || !name) {
      return null;
    }

    const id = String(idValue);

    return {
      Id: id,
      InvMasterId: id,
      ItemDescription: itemDescription || name,
      AddonQty: null,
      InventoryShort: inventoryShort || undefined,
      InvType: record.InvType,
      id,
      name,
    };
  }

  private mapAssignedPlanItem(item: unknown): SoapPlanItem | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const record = item as TreatmentSoapPlanItemRecord;
    const idValue = record.InvMasterId ?? record.Id;
    const inventoryShort = normalizePlanItemLabel(record.InventoryShort);
    const itemDescription = normalizePlanItemLabel(record.ItemDescription);
    const name = resolvePlanItemName(inventoryShort, itemDescription);
    if (!idValue || !name) {
      return null;
    }

    const id = String(idValue);

    return {
      Id: id,
      InvMasterId: record.InvMasterId ? String(record.InvMasterId) : undefined,
      ApptDetailId: record.ApptDetailId ? String(record.ApptDetailId) : undefined,
      TreatmentId: record.TreatmentId ? String(record.TreatmentId) : undefined,
      TreatmentPlanItemId: record.Id ? String(record.Id) : undefined,
      ItemDescription: itemDescription || name,
      AddonQty: this.normalizeAddonQty(record.AddonQty),
      InventoryShort: inventoryShort || undefined,
      id,
      name,
    };
  }

  private normalizeAddonQty(value: unknown): number | string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const trimmed = String(value).trim();
    if (!trimmed) {
      return null;
    }

    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : trimmed;
  }
}
